export interface AppUser {
  id: string;
  email: string;
  name?: string;
  emailVerified?: boolean;
}

export type AppAuthStatus =
  | "ready"
  | "signed_out"
  | "verification_required"
  | "pending_approval"
  | "declined"
  | "suspended"
  | "link_conflict";

export interface AuthClient {
  user(): AppUser | null;
}

export interface SessionClient {
  get(): Promise<AppUser | null>;
  status(): Promise<{ user: AppUser | null; status: AppAuthStatus }>;
}

export const AUTH_CONTEXT_KEY = "catchcompassUser";
const OFFLINE_AUTH_KEY = "catchcompass:offline-auth:v1";

function cacheOfflineUser(user: AppUser) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    OFFLINE_AUTH_KEY,
    JSON.stringify({ user, cached_at: new Date().toISOString() }),
  );
}

function readOfflineUser(): AppUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(OFFLINE_AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isAppUser(parsed?.user) ? parsed.user : null;
  } catch {
    return null;
  }
}

function clearOfflineUser() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(OFFLINE_AUTH_KEY);
}

function isAppUser(value: unknown): value is AppUser {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === "string" && typeof candidate.email === "string";
}

/**
 * Server-side identity adapter.
 * The standalone auth middleware sets AUTH_CONTEXT_KEY after Better Auth has
 * been validated and translated to the stable CatchCompass user identity.
 */
export function auth(c: any): AuthClient {
  return {
    user() {
      try {
        const value = c?.get?.(AUTH_CONTEXT_KEY);
        return isAppUser(value) ? value : null;
      } catch {
        return null;
      }
    },
  };
}

async function readAuthState(): Promise<{
  user: AppUser | null;
  status: AppAuthStatus;
}> {
  if (typeof window === "undefined")
    return { user: null, status: "signed_out" };

  try {
    const response = await fetch("/app-api/auth/session", {
      credentials: "include",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      const cached = readOfflineUser();
      if (typeof navigator !== "undefined" && !navigator.onLine && cached)
        return { user: cached, status: "ready" };
      clearOfflineUser();
      return { user: null, status: "signed_out" };
    }
    const payload = await response.json();
    const user = isAppUser(payload?.user) ? payload.user : null;
    const status = ([
      "ready",
      "signed_out",
      "verification_required",
      "pending_approval",
      "declined",
      "suspended",
      "link_conflict",
    ] as const).includes(payload?.status)
      ? payload.status
      : user
        ? "ready"
        : "signed_out";
    if (user && status === "ready") cacheOfflineUser(user);
    else clearOfflineUser();
    return { user, status };
  } catch {
    const cached = readOfflineUser();
    return cached
      ? { user: cached, status: "ready" }
      : { user: null, status: "signed_out" };
  }
}

/** Browser-side identity surface used by the existing CatchCompass pages. */
export const session: SessionClient = {
  async get() {
    return (await readAuthState()).user;
  },
  status: readAuthState,
};

async function authRequest(path: string, body: Record<string, unknown>) {
  const response = await fetch(`/api/auth/${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = payload?.message || payload?.error || "Authentication failed";
    throw new Error(String(message));
  }
  return payload;
}

export async function signInWithEmail(email: string, password: string) {
  return authRequest("sign-in/email", { email, password, rememberMe: true });
}

export async function signUpWithEmail(
  name: string,
  email: string,
  password: string,
) {
  return authRequest("sign-up/email", { name, email, password });
}

export async function sendVerificationEmail(email: string) {
  return authRequest("send-verification-email", {
    email,
    callbackURL: "/login",
  });
}

export async function requestPasswordReset(email: string) {
  const redirectTo =
    typeof window === "undefined"
      ? "/login"
      : `${window.location.origin}/login?catchcompassReset=1`;
  return authRequest("request-password-reset", { email, redirectTo });
}

export async function resetPassword(newPassword: string, token: string) {
  return authRequest("reset-password", { newPassword, token });
}

/** Change an authenticated user's password and revoke every other session. */
export async function changePassword(currentPassword: string, newPassword: string) {
  return authRequest("change-password", {
    currentPassword,
    newPassword,
    revokeOtherSessions: true,
  });
}

export async function deleteOwnAccount(password: string) {
  const result = await authRequest("delete-user", { password });
  clearOfflineUser();
  return result;
}

/**
 * End the Better Auth session, prove that the session cookie is gone, and only
 * then return to the CatchCompass front door. A failed sign-out must never be
 * disguised as success by an unconditional redirect.
 */
export function signOut(): void {
  if (typeof window === "undefined") return;

  void (async () => {
    try {
      const response = await fetch("/api/auth/sign-out", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: "{}",
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message =
          payload?.message ||
          payload?.error ||
          `Sign out failed (${response.status})`;
        throw new Error(String(message));
      }

      const state = await readAuthState();
      if (state.status !== "signed_out" || state.user) {
        throw new Error("Sign out did not clear the CatchCompass session.");
      }

      clearOfflineUser();
      window.location.replace("/");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "CatchCompass could not sign you out. Please try again.";
      console.error("CatchCompass sign out failed", error);
      window.alert(message);
    }
  })();
}
